// Copyright (c) StrangeBee 2024

import { ICONS } from '@Constants/iconsApp';
import { THRadio } from '@Molecules/Inputs/THRadio/THRadio';
import React, { ReactElement, useMemo, useState } from 'react';
import { DonutWidget } from '@Molecules/Widgets/DonutWidget';
import { WidgetOutput } from 'thehive-sdk';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { createWidgetQueryKey } from '@Molecules/Widgets/utils';
import { widgetQueries } from '@Organisms/Dashboard/DashboardView/constants';
import { useGenericQuery } from '@Queries/genericQueries';

import './StatsCard.scss';

export interface StatsCardProps {
    title: string;
    widget: WidgetOutput;
    onItemClick: (value: any) => void;
}

const StatsCard = React.memo(({ title, widget, onItemClick }: StatsCardProps): ReactElement | null => {
    const [displayMode, setDisplayMode] = useState<'table' | 'chart'>('chart');

    const query = useGenericQuery({
        fetchCallback: () =>
            widgetQueries.donut({
                options: widget.options,
            }),
        key: [createWidgetQueryKey(widget)],
        manual: true,
        fetchOnStart: true,
    });

    const donutWidget = useMemo(
        () => (
            <DonutWidget
                query={query}
                widget={widget}
                hasDownload={false}
                onClick={(value) => {
                    const valueParsed = widget.options.customize
                        ? widget.options.customize?.find(({ label }) => label + '' === value + '')?.name
                        : value;

                    onItemClick(valueParsed);
                }}
            />
        ),
        [onItemClick, widget, query],
    );

    return (
        <div className="stats-card">
            <div className="stats-card__title">
                <span>{title}</span>
                <THRadio.Group value={displayMode} onChange={(e) => setDisplayMode(e.target.value)} size="small">
                    <THRadio.Button value="table">
                        <ICONS.TableOutlined />
                    </THRadio.Button>
                    <THRadio.Button value="chart">
                        <ICONS.PieChartOutlined />
                    </THRadio.Button>
                </THRadio.Group>
            </div>
            {displayMode === 'chart' && <div className="stats-card__widget">{donutWidget}</div>}

            {displayMode === 'table' && (
                <div className="stats-card__table">
                    <div>
                        {query?.data?.reduce((acc, { _key, count }) => {
                            if (count === 0) {
                                return acc;
                            }

                            const label = widget.options.customize
                                ? widget.options.customize?.find(({ name }) => name + '' === _key + '')?.label
                                : _key;

                            return [
                                ...acc,
                                <div className="stats-card__table__item" key={_key}>
                                    <THButton type="link" onClick={() => onItemClick(_key)}>
                                        {label + ''}
                                    </THButton>
                                    <span>{count}</span>
                                </div>,
                            ];
                        }, [] as ReactElement[])}
                    </div>
                </div>
            )}
        </div>
    );
});

export default StatsCard;
